import React, { useState } from "react";
import styles from "./Archived.module.scss";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../components/Table/Table";
import Button from "../../components/Button/Button";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/utils";
import Swal from "sweetalert2";
import { getOrganizationData } from "../../actions/organization";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import { showOverlay, hideOverlay } from "../../redux/overlayLoaderStore";
import {
  resetSelection,
  clearResetSelection,
} from "../../redux/rowSelectionStore";
import { ReactComponent as UpArrowIcon } from "../../icons/up-arrow.svg";
import { ReactComponent as DownArrowIcon } from "../../icons/down-arrow.svg";
import { formatDate } from "../../utils/helpers";
import _ from "lodash";
import classNames from "classnames";
import SecondaryButton from "../../components/Button/SecondaryButton";
import Input from "../../components/Input/Input";
import { ReactComponent as SearchIcon } from "../../icons/search.svg";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";

const Archived = () => {
  const { orgId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionToken = localStorage.getItem("session_token");

  const [expanded, setExpanded] = useState({
    audits: true,
    findings: true,
    improvements: true,
  });

  const [selectedAudits, setSelectedAudits] = useState([]);
  const [selectedFindings, setSelectedFindings] = useState([]);
  const [selectedImprovements, setSelectedImprovements] = useState([]);
  const [searchQueries, setSearchQueries] = useState({
    audits: "",
    findings: "",
    improvements: "",
  });

  const { auditsTasksData, findingsData, improvementsData } = useSelector(
    (state) => state.organization,
  );
  // Extracting path from URL
  const lastIndex = location.pathname.lastIndexOf("/");
  const modifiedPath = location.pathname.substring(lastIndex);

  const modules = useSelector((state) => state.user.modules[orgId]);
  const currentModule = modules?.find((mod) => mod.path === modifiedPath);

  const archivedAudits = Object.values(auditsTasksData || {}).filter(
    (a) => a.archived,
  );
  const archivedFindings = Object.values(findingsData || {}).filter(
    (f) => f.archived,
  );
  const archivedImprovements = Object.values(improvementsData || {}).filter(
    (i) => i.archived,
  );

  const resetRowSelection = useSelector(
    (state) => state.rowSelection.resetSelection,
  );

  const handleUnarchive = async (items, type) => {
    if (!items.length) return;

    const payload = items.map((item) => ({ id: item.id, archived: false }));
    const endpointMap = {
      audits: "/nocodb/audit_tasks",
      findings: "/nocodb/findings",
      improvements: "/nocodb/improvements",
    };

    const loadFlags = {
      audits: [false, false, true], // loadAuditTasks
      findings: [true, false, false], // loadFindings
      improvements: [false, true, false], // loadImprovements
    };

    dispatch(showOverlay());

    try {
      await axiosInstance.patch(
        `${endpointMap[type]}?org_id=${orgId}`,
        payload,
        {
          headers: { "session-token": sessionToken },
        },
      );

      const [loadFindings, loadImprovements, loadAuditTasks] =
        loadFlags[type] || [];

      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          loadFindings,
          loadImprovements,
          loadAuditTasks,
          false,
          false,
        ),
      );

      const unarchivedKeys = items.map((item) => item.key).join(", ");

      Swal.fire({
        toast: true,
        icon: "success",
        title: `Unarchive successful`,
        text: `Keys unarchived: ${unarchivedKeys}`,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } catch (err) {
      Swal.fire({
        toast: true,
        icon: "error",
        title: `Unarchive failed`,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      dispatch(hideOverlay());
      dispatch(resetSelection());
      setTimeout(() => dispatch(clearResetSelection()), 100);
    }
  };

  const truncateText = (text, maxLength) => {
    if (text?.length <= maxLength) {
      return text;
    }
    return (
      <>
        {text?.substring(0, maxLength)}
        <span className={styles.readMore}>... read more</span>
      </>
    );
  };

  const renderSection = (
    label,
    data,
    columns,
    selected,
    setSelected,
    type,
    searchQuery,
  ) => {
    const searchQueryLower = searchQuery?.toLowerCase() || "";

    const filteredData = data.filter((item) => {
      return columns.some((col) => {
        const accessor = col.accessorKey;
        if (!accessor) return false;

        const value = accessor.includes(".")
          ? accessor.split(".").reduce((obj, key) => obj?.[key], item)
          : item?.[accessor];

        return value?.toString().toLowerCase().includes(searchQueryLower);
      });
    });

    return (
      <div className={styles.section}>
        <SecondaryButton
          text={expanded[type] ? `Hide ${label}` : `Show ${label}`}
          onClick={() =>
            setExpanded((prev) => ({ ...prev, [type]: !prev[type] }))
          }
          className={styles.collapsibleButton}
          icon={expanded[type] ? <UpArrowIcon /> : <DownArrowIcon />}
          iconPosition="right"
        />
        {expanded[type] && (
          <>
            <div className={styles.actionsRow}>
              <Button
                text="Unarchive"
                onClick={() => handleUnarchive(selected, type)}
                disabled={!selected.length}
                className={styles.button}
              />
              <Input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) =>
                  setSearchQueries((prev) => ({
                    ...prev,
                    [type]: e.target.value,
                  }))
                }
                className={styles.input}
                icon={SearchIcon}
              />
            </div>
            {filteredData.length === 0 ? (
              <div className={styles.noDataMessage}>
                There are no archived data.
              </div>
            ) : (
              <Table
                columns={columns}
                data={filteredData}
                onSelectedRowsChange={setSelected}
                resetSelection={resetRowSelection}
                centered
              />
            )}
          </>
        )}
      </div>
    );
  };

  const getArchivedColumns = (type, styles) => {
    const selectColumn = {
      accessorKey: "select",
      header: ({ table }) => (
        <div
          className={styles.customCheckboxContainer}
          onClick={(e) => {
            e.stopPropagation();
            table.toggleAllPageRowsSelected(!table.getIsAllPageRowsSelected());
          }}
        >
          {table.getIsAllPageRowsSelected() ? (
            <div className={styles.checked} />
          ) : (
            <div className={styles.unchecked} />
          )}
        </div>
      ),
      size: 50,
      cell: ({ row }) => (
        <div className={styles.customCheckboxContainer}>
          {row.getIsSelected() ? (
            <div className={styles.checked} />
          ) : (
            <div className={styles.unchecked} />
          )}
        </div>
      ),
      enableSorting: false,
    };

    if (type === "audits") {
      return [
        selectColumn,
        { accessorKey: "key", header: "Key", size: 90 },
        {
          accessorKey: "name",
          header: "Audit Name",
          size: 200,
          enableSorting: false,
        },
        {
          accessorKey: "responsible",
          header: "Responsible",
          size: 180,
          cell: (props) => {
            return props.row.original.responsible ? (
              <div>{props.row.original.responsible}</div>
            ) : (
              <div>Not defined</div>
            );
          },
          enableSorting: false,
        },
        {
          accessorKey: "status",
          header: "Status",
          cell: (props) => {
            const status = props.getValue();
            let statusClassName = "";

            switch (status) {
              case "Open":
                statusClassName = styles.openStatus;
                break;
              case "In Progress":
                statusClassName = styles.inProgressStatus;
                break;
              case "In Review":
                statusClassName = styles.inReviewStatus;
                break;
              case "Done":
                statusClassName = styles.resolvedStatus;
                break;
              default:
                break;
            }

            return (
              <div className={classNames(styles.statusCell, statusClassName)}>
                {status}
              </div>
            );
          },
          enableSorting: false,
        },
        {
          accessorKey: "date",
          header: "Due Date",
          cell: (props) => <div>{formatDate(props.getValue())}</div>,
          enableSorting: false,
        },
        {
          accessorKey: "frequency",
          header: "Frequency",
          cell: (props) => (
            <div>
              {props.row.original?.frequency?.charAt(0).toUpperCase() +
                props.row.original?.frequency?.slice(1)}
            </div>
          ),
          enableSorting: false,
        },
        {
          accessorKey: "chapter",
          header: "Chapter",
          cell: (props) => {
            const uniqueRisks = _.uniqBy(
              props.row.original.form,
              (item) => `${item.risk?.qms_id} - ${item.risk?.qms_description}`,
            );
            return (
              <div>
                {uniqueRisks.map((item, index) => (
                  <div key={index}>
                    {item.risk?.qms_id} - {item.risk?.qms_description}
                  </div>
                ))}
              </div>
            );
          },
          size: 280,
          enableSorting: false,
        },
      ];
    }

    if (type === "findings") {
      return [
        selectColumn,
        { accessorKey: "key", header: "Key", size: 90 },
        {
          accessorKey: "reference",
          header: "Reference",
          size: 170,
          enableSorting: false,
        },
        {
          accessorKey: "risk.risk_id",
          header: "Risk",
          size: 100,
          enableSorting: false,
        },
        {
          accessorKey: "risk.level",
          header: "Level",
          size: 100,
          cell: (props) => {
            const level = props.row.original?.risk?.level || "unknown"; // Fallback if level is undefined
            const formattedLevel =
              level.charAt(0).toUpperCase() + level.slice(1).toLowerCase(); // Format to uppercase first letter
            return <div>{formattedLevel}</div>;
          },
          enableSorting: false,
        },
        {
          accessorKey: "description",
          header: "Description",
          size: 300,
          cell: (props) => <div>{truncateText(props.getValue(), 80)}</div>,
          enableSorting: false,
        },
        {
          accessorKey: "responsible",
          header: "Responsible",
          size: 170,
          cell: (props) => {
            return props.row.original.responsible ? (
              <div>{props.row.original.responsible}</div>
            ) : (
              <div>Not defined</div>
            );
          },
          enableSorting: false,
        },
        {
          accessorKey: "status",
          header: "Status",
          size: 120,
          cell: (props) => {
            const status = props.getValue();
            let statusClassName = "";

            switch (status) {
              case "Open":
                statusClassName = styles.openStatus;
                break;
              case "In Progress":
                statusClassName = styles.inProgressStatus;
                break;
              case "In Review":
                statusClassName = styles.inReviewStatus;
                break;
              case "Done":
                statusClassName = styles.resolvedStatus;
                break;
              default:
                break;
            }

            return (
              <div className={classNames(styles.statusCell, statusClassName)}>
                {status}
              </div>
            );
          },
          enableSorting: false,
        },
        {
          accessorKey: "date",
          header: "Date",
          cell: (props) => <div>{formatDate(props.getValue())}</div>,
          enableSorting: false,
        },
      ];
    }

    if (type === "improvements") {
      return [
        selectColumn,
        {
          accessorKey: "key",
          header: "Key",
          size: 80,
          cell: (props) => <div>{props.getValue()}</div>,
          enableSorting: true,
          sortingFn: "alphanumeric",
        },
        {
          accessorKey: "name",
          header: "Name",
          size: 200,
          cell: (props) => <div>{truncateText(props.getValue(), 60)}</div>,
          enableSorting: false,
        },
        {
          accessorKey: "description",
          header: "Description",
          size: 350,
          cell: (props) => <div>{truncateText(props.getValue(), 80)}</div>,
          enableSorting: false,
        },
        {
          accessorKey: "risks",
          header: "Risks",
          size: 150,
          cell: (props) => {
            const linkedFindingKeys = props.row.original?.linked_findings || [];

            const linkedRisks = linkedFindingKeys
              .map((findingKey) => {
                const finding = Object.values(findingsData || {}).find(
                  (f) => f.key === findingKey,
                );
                return finding?.risk;
              })
              .filter((risk) => risk && risk.risk_id)
              .filter(
                (risk, index, self) =>
                  self.findIndex((r) => r.risk_id === risk.risk_id) === index,
              )
              .sort((a, b) => a.risk_id.localeCompare(b.risk_id));

            if (linkedRisks.length === 0) {
              return <div>Not defined</div>;
            }

            return (
              <div className={styles.risksCell}>
                {linkedRisks.map((risk, index) => (
                  <span key={risk.risk_id} className={styles.riskItem}>
                    {risk.risk_id}
                    {index !== linkedRisks.length - 1 && <span>, </span>}
                  </span>
                ))}
              </div>
            );
          },
          enableSorting: false,
        },
        {
          accessorKey: "date",
          header: "Date",
          size: 130,
          cell: (props) => (
            <div>
              {props.getValue() ? formatDate(props.getValue()) : "Not defined"}
            </div>
          ),
          sortingFn: "datetime",
        },
        {
          accessorKey: "responsible",
          header: "Responsible",
          size: 170,
          cell: (props) => {
            return props.row.original.responsible ? (
              <div>{props.row.original.responsible}</div>
            ) : (
              <div>Not defined</div>
            );
          },
          enableSorting: false,
        },
        {
          accessorKey: "status",
          header: "Status",
          size: 120,
          cell: (props) => {
            const status = props.getValue();
            let statusClassName = "";

            switch (status) {
              case "Open":
                statusClassName = styles.openStatus;
                break;
              case "In Progress":
                statusClassName = styles.inProgressStatus;
                break;
              case "In Review":
                statusClassName = styles.inReviewStatus;
                break;
              case "Done":
                statusClassName = styles.resolvedStatus;
                break;
              default:
                break;
            }

            return (
              <div className={classNames(styles.statusCell, statusClassName)}>
                {status}
              </div>
            );
          },
          enableSorting: false,
        },
      ];
    }

    return [selectColumn];
  };

  const auditColumns = getArchivedColumns("audits", styles);
  const findingColumns = getArchivedColumns("findings", styles);
  const improvementColumns = getArchivedColumns("improvements", styles);

  return (
    <MainLayout>
      <div className={styles.column}>
        <Breadcrumbs data={currentModule} />
        <div className={styles.archivedContainer}>
          {renderSection(
            "Archived Audits",
            archivedAudits,
            auditColumns,
            selectedAudits,
            setSelectedAudits,
            "audits",
            searchQueries.audits,
          )}
          {renderSection(
            "Archived Findings",
            archivedFindings,
            findingColumns,
            selectedFindings,
            setSelectedFindings,
            "findings",
            searchQueries.findings,
          )}
          {renderSection(
            "Archived Improvement Projects",
            archivedImprovements,
            improvementColumns,
            selectedImprovements,
            setSelectedImprovements,
            "improvements",
            searchQueries.improvements,
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Archived;
